import React from 'react';
import { observer } from 'mobx-react';

// Modals
import ShareModal from '../../../MyWorkspace/components/Modals/ShareModal';
import DuplicateModal from '../../../MyWorkspace/components/Modals/DuplicateModal';
import AssignAgentModal from '../../../MyWorkspace/components/Modals/AssignAgentModal';
import RenameModal from '../../../../components/RenameModal';
import RenameFolderModal from '../../../../components/RenameFolderModal';
import RenameDocumentModal from '../../../../components/RenameDocumentModal/RenameDocumentModal';
import MoveFolderModal from '../../../../components/MoveFolderModal';
import DeleteDocumentModal from '../../../../components/DeleteDocumentModal';
import NewFolderModal from '../../../../components/NewFolderModal';

// Slideouts
import DocumentDetailSlideout from '../../../../components/DocumentDetailSlideout/DocumentDetailSlideout';
import FolderInformationSlideout from '../../../../components/FolderInformationSlideout/FolderInformationSlideout';

/**
 * Component that renders all modals and slideouts for DriveWorkspace
 * Centralizes modal management to keep the main component clean
 */
const DriveWorkspaceModals = observer(({
  // Store
  driveWorkspaceStore,
  resourcesStore,
  
  // Vault UUID for new folder
  vaultUuid,
  
  // Store-based modal states (from driveWorkspaceStore)
  isShareModalOpen,
  isDuplicateModalOpen,
  isAssignAgentModalOpen,
  isMoveModalOpen,
  isDeleteModalOpen,
  documentToDeleteName,
  isDeletingDocument,
  usersList,
  
  // Local modal states
  isRenameFolderModalOpen,
  folderToRename,
  isRenameDocumentModalOpen,
  documentToRename,
  showNewFolderModal,
  
  // Slideout states
  isDocumentDetailOpen,
  selectedDocument,
  isFolderInfoOpen,
  selectedFolder,
  
  // Share modal handlers
  onCloseShareModal,
  onShareAll,
  onAddToPending,
  onUpdatePendingPermission,
  onRemovePending,
  onUpdatePermission,
  onRemoveAccess,
  
  // Duplicate modal handlers
  onCloseDuplicateModal,
  onDuplicateItem,
  
  // Assign agent modal handlers
  onCloseAssignAgentModal,
  onAssignAgent,
  
  // Move modal handlers
  onCloseMoveModal,
  
  // Delete modal handlers
  onCloseDeleteModal,
  onConfirmDelete,
  
  // Rename folder modal handlers
  onCloseRenameFolderModal,
  onSaveRenameFolder,
  
  // Rename document modal handlers
  onCloseRenameDocumentModal,
  onSaveRenameDocument,
  onOpenRenameDocumentModal,
  
  // New folder modal handlers
  onCloseNewFolderModal,
  onCreateFolder,
  
  // Document detail slideout handlers
  onCloseDocumentDetail,
  closeSlideoutThen,
  onShareModalOpen,
  onAssignAgentModalOpen,
  
  // Folder info slideout handlers
  onCloseFolderInfo,
}) => {
  return (
    <>
      {/* Rename Modals */}
      <RenameModal store={driveWorkspaceStore.renameDocumentModalStore} />
      
      <RenameFolderModal
        isOpen={isRenameFolderModalOpen}
        onClose={onCloseRenameFolderModal}
        onSave={onSaveRenameFolder}
        folderUuid={folderToRename?.folder_uuid}
        initialName={folderToRename?.name || ''}
      />

      <RenameDocumentModal
        isOpen={isRenameDocumentModalOpen}
        onClose={onCloseRenameDocumentModal}
        onSave={onSaveRenameDocument}
        documentUuid={
          documentToRename?.document_uuid ||
          documentToRename?.documentUuid ||
          documentToRename?.id
        }
        initialName={documentToRename?.name || ''}
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        store={driveWorkspaceStore}
        resourcesStore={resourcesStore}
        onClose={onCloseShareModal}
        onShare={onShareAll}
        onAddToPending={onAddToPending}
        onUpdatePendingPermission={onUpdatePendingPermission}
        onRemovePending={onRemovePending}
        onUpdateGranteePermission={onUpdatePermission}
        onRemoveGrantee={onRemoveAccess}
        usersList={usersList}
      />

      {/* Duplicate Modal */}
      <DuplicateModal
        isOpen={isDuplicateModalOpen}
        store={driveWorkspaceStore}
        onClose={onCloseDuplicateModal}
        onConfirm={onDuplicateItem}
      />

      {/* Assign Agent Modal */}
      <AssignAgentModal
        isOpen={isAssignAgentModalOpen}
        store={driveWorkspaceStore}
        onClose={onCloseAssignAgentModal}
        onConfirm={onAssignAgent}
      />

      {/* Move Modal */}
      <MoveFolderModal
        isOpen={isMoveModalOpen}
        onClose={onCloseMoveModal}
        onSave={onCloseMoveModal}
        isSubmitting={false}
      />

      {/* Document Detail Slideout */}
      <DocumentDetailSlideout
        open={isDocumentDetailOpen}
        onClose={onCloseDocumentDetail}
        document={selectedDocument}
        showActions
        store={driveWorkspaceStore}
        onRename={onOpenRenameDocumentModal}
        onShare={(item, type) => {
          closeSlideoutThen(() => {
            onShareModalOpen(item, type);
          });
        }}
        onManageAccess={(doc) => {
          closeSlideoutThen(() => {
            onShareModalOpen(doc, 'document');
          });
        }}
        onManageAgent={(doc) => {
          closeSlideoutThen(() => {
            onAssignAgentModalOpen(doc);
          });
        }}
      />

      {/* Folder Information Slideout */}
      <FolderInformationSlideout
        open={isFolderInfoOpen}
        onClose={onCloseFolderInfo}
        folderName={selectedFolder?.name}
        ownerName={selectedFolder?.owner_full_name || selectedFolder?.owner || 'Unknown'}
      />

      {/* Delete Document Modal */}
      <DeleteDocumentModal
        isOpen={isDeleteModalOpen}
        onClose={onCloseDeleteModal}
        onConfirm={onConfirmDelete}
        documentName={documentToDeleteName}
        isDeleting={isDeletingDocument}
      />

      {/* New Folder Modal */}
      <NewFolderModal
        isOpen={showNewFolderModal}
        onClose={onCloseNewFolderModal}
        onSave={onCreateFolder}
        vaultUuid={vaultUuid}
        folderParentUuid={driveWorkspaceStore.rootFolderUuid ?? ""}
        store={driveWorkspaceStore}
      />
    </>
  );
});

DriveWorkspaceModals.displayName = 'DriveWorkspaceModals';

export default DriveWorkspaceModals;
